<template>
    <div>
        <ul v-if="debug" style="font-size: 12px; position: absolute; right: 0">
            <li>Item id: {{ currentItem ? currentItem.id : 'None' }}</li>
            <li>Volume: {{ volume.toFixed(2) }}/{{ maxVolume }}</li>
            <li>Transition start: {{ currentItem ? currentItem.transitionStart : '-' }}</li>
            <li>Transition end: {{ currentItem ? currentItem.transitionEnd : '-' }}</li>
            <li>Changing: {{ isChangingItem }}</li>
        </ul>

        <video ref="audioPlayer" class="video-js vjs-audio-only-skin" :class="{ 'song-player': !debug, 'song-player-debug': debug }"></video>
    </div>

</template>

<script>

    import { cloneDeep, ceil } from 'lodash';
    import videojs from 'video.js';
    import { RENDER_STATES } from '../enums';
    import { PLAYER_COMMUNICATION } from '../../enums/player-communication';

    export default {
        props: {
            communication: {
                type: String,
                default: PLAYER_COMMUNICATION.WEBSOCKET,
            },

            muted: {
                type: Boolean,
                default: false,
            },

            debug: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                player: null,

                currentItem: null,
                isChangingItem: false,

                volume: 0,
                maxVolume: 1,

                autoPlay: false,

                currentMuteInterval: null,
                currentUnMuteInterval: null,
            };
        },

        mounted() {
            this.player = this.createPlayer();
        },

        methods: {
            setActiveItem({ item, options, duration }) {
                // Nothing to play
                if (item === null) {
                    this.changeItem(null, options, duration);
                } else {
                    this.changeItem(cloneDeep(item), options, duration);
                }
            },

            setRenderState({ renderState }) {
                console.log('[audio] set render state to ' + renderState);

                if (this.isChangingItem && (renderState === RENDER_STATES.FULL || renderState === RENDER_STATES.AUDIO_ONLY)) {
                    return;
                }

                if (renderState === RENDER_STATES.FULL || renderState === RENDER_STATES.AUDIO_ONLY) {
                    this.play();
                } else {
                    this.pause();
                }
            },

            createPlayer() {
                const player = videojs(this.$refs.audioPlayer, {
                    autoplay: this.autoPlay,
                    loop: false,
                    controls: this.debug,
                    controlBar: this.debug ? {
                        fullscreenToggle: false,
                        pictureInPictureToggle: false,
                    } : false,
                    preload: this.preload,
                    width: '100%',
                    height: '30px',

                    sources: [],

                    volume: 0,
                    muted: this.muted,
                });

                player.on('loadeddata', () => {
                    console.log('[audio] Audio autoload (loadeddata), paused: ' + (this.player.paused() ? 'yes' : 'no') + ', autoplay: ' + (this.autoPlay ? 'yes' : 'no'));

                    if (this.autoPlay && this.player.paused()) {
                        console.log('[audio] Audio autoload via loadeddata');

                        this.play();
                    }
                });

                return player;
            },

            async updatePlayer(item, duration = 0) {
                if (item) {
                    if (this.communication === PLAYER_COMMUNICATION.IPC) {
                        // For the lite player we have to download the file ourselves because we use a custom url schema for retrieving the locally saved file.
                        const response = await this.$http.get(item.src, {
                            responseType: 'blob',
                        });

                        this.player.src({
                            src: URL.createObjectURL(response.data),
                            type: 'audio/mp3',
                        });

                    } else {
                        this.player.src({
                            src: item.src,
                            type: 'audio/mp3',
                        });
                    }

                    this.player.loop(item.repeat);
                }

                this.player.currentTime(duration);
            },

            async changeItem(item, options = null, duration = 0) {
                console.log('[audio] Change audio item to: ' + item?.id + ', current: ' + this.currentItem?.id);

                this.isChangingItem = true;

                try {
                    if (this.currentItem) {
                        // When the audio is still playing we want to mute the current
                        if (!this.player.paused()) {
                            // First fade out the current audio
                            await this.muteCurrent(options?.transitionEnd ?? this.currentItem.transitionEnd);

                            // After the fade update the previous item and emit the transition ended event
                            this.$emit('transition-end', this.currentItem.id);

                            console.log('[audio] Muted current audio');
                        } else {
                            // When we are not playing any audio just set the volume to zero and mark the transition end as finished
                            this.setCurrentVolume(0);

                            // After the fade update the previous item and emit the transition ended event
                            this.$emit('transition-end', this.currentItem.id);
                            console.log('[audio] Volume was still above zero but nothing playing, setting volume to zero');
                        }
                    }

                    // Change the current item to the new item
                    this.currentItem = item;

                    if (item) {
                        this.$emit('transition-start', item.id);
                    }

                    await this.updatePlayer(item, duration);

                    console.log('[audio] Started audio ' + this.currentItem?.id);

                    if (this.currentItem) {
                        await this.play(0, options);
                    } else {
                        await this.player.pause();
                    }
                } catch (exception) {
                    console.log('[audio] error while changing item: ' + exception.message);
                    console.error(exception);
                }

                this.isChangingItem = false;
            },

            async play(attempts = 0, options = null) {
                console.log('[audio] play (' + attempts + ')');

                try {
                    await this.player.play();
                } catch (exception) {
                    const maxAttempts = 100;

                    if (attempts > maxAttempts) {
                        console.log('[audio] Play failed after ' + attempts + '/' + maxAttempts + ' attempts');
                        throw exception;
                    }

                    console.log('[audio] Play failed ' + attempts + '/' + maxAttempts + ' attempts, retry in 10ms');

                    // Browsers...
                    await new Promise(resolve => setTimeout(() => resolve(), 10));

                    await this.play(attempts + 1, options);
                }

                if (this.currentItem) {
                    // Unmute
                    await this.unMuteCurrent(options?.transitionStart ?? this.currentItem.transitionStart);
                }
            },

            async pause() {
                console.log('[audio] pause');
                if (this.currentItem) {
                    // Mute
                    await this.muteCurrent(this.currentItem.transitionEnd);
                }

                await this.player.pause();
            },

            setCurrentVolume(volume) {
                let newVolume = volume >= this.maxVolume ? this.maxVolume : volume;

                if (newVolume <= 0) {
                    newVolume = 0;
                } else if (newVolume >= 1) {
                    newVolume = 1;
                }

                this.volume = newVolume;

                console.log('[audio] Setting volume: ' + this.volume.toFixed(2) + '/' + this.maxVolume);
                this.player.volume(this.volume);
            },

            muteCurrent(transitionTime) {
                this.clearUnMuteInterval();
                this.clearMuteInterval();

                return new Promise((resolve) => {
                    this.currentMuteInterval = setInterval(() => {
                        const newVolume = this.volume - ceil(100 / transitionTime, 2);

                        this.setCurrentVolume(newVolume);

                        if (this.volume <= 0) {

                            resolve();
                            this.clearMuteInterval();
                        }
                    }, 100);
                });
            },

            unMuteCurrent(transitionTime) {
                this.clearUnMuteInterval();
                this.clearMuteInterval();

                return new Promise((resolve) => {
                    this.currentUnMuteInterval = setInterval(() => {
                        const newVolume = this.volume + ceil((100 / transitionTime), 2);

                        this.setCurrentVolume(newVolume);

                        if (this.volume >= this.maxVolume) {
                            this.clearUnMuteInterval();

                            resolve();
                        }
                    }, 100);
                });
            },

            clearMuteInterval() {
                if (this.currentMuteInterval) {
                    clearInterval(this.currentMuteInterval);
                    this.currentMuteInterval = null;
                }
            },

            clearUnMuteInterval() {
                if (this.currentUnMuteInterval) {
                    clearInterval(this.currentUnMuteInterval);
                    this.currentUnMuteInterval = null;
                }
            },
        },

        beforeDestroy() {
            this.clearUnMuteInterval();
            this.clearMuteInterval();

            this.player.dispose();
        },
    };

</script>

<style lang="scss">

    .vjs-audio-only-skin {
        width: 100% !important;
        min-width: 200px !important;
        height: 30px !important;
        background: transparent !important;

        .vjs-big-play-button {
            display: none !important;
        }

        .vjs-control-bar {
            display: flex !important;
            border-radius: 30px !important;
            background-color: #e3e3e3 !important;
            color: #000 !important;
            opacity: 1 !important;
        }
    }

</style>

<style lang="scss" scoped>

    .song-player {
        opacity: 0;
        pointer-events: none;
        top: 0;
        left: 0;
        width: 10px;
        height: 10px;
        overflow: hidden;
        position: absolute;
    }

    .song-player-debug {
        position: fixed;
        bottom: 0;
        right: 0;
        width: 300px !important;
        z-index: 1001;
        pointer-events: all;
    }

</style>
